import { useMemo } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useUI } from '../../context/UIContext'
import { cn } from '@/lib/utils'
import { useOrganisationDisplayName } from '../../context/OrganisationDisplayContext'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { useContactUnreadTotal } from '@/hooks/useContactUnreadTotal'
import { usePosNavVisible } from '@/hooks/usePosNavVisible'
import { posSidebarNavItems, storeSidebarNav } from '@/config/sidebarNav'
import { APP_DISPLAY_NAME } from '@/lib/appDisplayName'
import { staffHasModule } from '@/config/staffModules'
import { resolveBillingAccessMode } from '@/lib/billingAccess'
import { SidebarNavSections } from './SidebarNavSections'
import { StoreSwitcher } from './StoreSwitcher'

export function Sidebar() {
  const { sidebarOpen, closeSidebar } = useUI()
  const { user } = useAuth()
  const { currentStore } = useCurrentStore()
  const { organisationDisplayName } = useOrganisationDisplayName()
  const passwordLocked = Boolean(user?.mustChangePassword)
  const billingMode = resolveBillingAccessMode({
    billingRequired: currentStore?.billingRequired || user?.billingRequired,
    subscriptionStatus: currentStore?.subscription?.status,
  })
  const billingLocked = !passwordLocked && billingMode === 'hard'
  const navLocked = passwordLocked || billingLocked
  const { total: contactUnreadTotal } = useContactUnreadTotal(!navLocked)
  const posVisible = usePosNavVisible(!navLocked)

  const segments = useMemo(() => {
    const isOrgAdmin = user?.role === 'org_admin'
    const modules = user?.modules

    const filtered = storeSidebarNav
      .map((segment) => ({
        ...segment,
        items: segment.items.filter((item) => {
          if (item.orgAdminOnly && !isOrgAdmin) return false
          if (isOrgAdmin || user?.role !== 'staff') return true
          if (!item.staffModule) return true
          return staffHasModule(modules, item.staffModule)
        }),
      }))
      .filter((segment) => segment.items.length > 0)

    const posAllowed =
      posVisible && (isOrgAdmin || staffHasModule(modules, 'pos'))
    if (!posAllowed) return filtered
    return [...filtered, { title: 'POS', items: posSidebarNavItems }]
  }, [posVisible, user?.role, user?.modules])

  return (
    <>
      {sidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 cursor-pointer bg-background/60 backdrop-blur-sm transition-opacity duration-200 lg:hidden"
          aria-label="Close menu"
          onClick={closeSidebar}
        />
      ) : null}

      <aside
        className={cn(
          'glass-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-40 flex h-dvh w-[17rem] shrink-0 flex-col transition-transform duration-200 ease-out lg:relative lg:h-full lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-sidebar-border px-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25">
            {APP_DISPLAY_NAME.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {APP_DISPLAY_NAME.replace(/\s+Store Admin$/i, '')}
            </p>
            <p className="truncate text-sm font-semibold text-sidebar-foreground">Store Admin</p>
            {organisationDisplayName ? (
              <p
                className="mt-0.5 truncate text-xs text-muted-foreground"
                title={organisationDisplayName}
              >
                {organisationDisplayName}
              </p>
            ) : null}
          </div>
        </div>

        <div className="overflow-hidden border-b border-sidebar-border px-4 py-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Current store
          </p>
          <StoreSwitcher className="w-full max-w-none" layout="stacked" />
        </div>

        <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3" aria-label="Main navigation">
          <SidebarNavSections
            segments={segments}
            passwordLocked={passwordLocked}
            billingLocked={billingLocked}
            contactUnreadTotal={contactUnreadTotal}
            onNavigate={closeSidebar}
          />
        </nav>

        <div className="shrink-0 border-t border-sidebar-border px-5 py-4">
          <p className="text-[11px] leading-relaxed text-muted-foreground">Business operations workspace</p>
        </div>
      </aside>
    </>
  )
}
